import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trackOrder } from "../../services/apis";
import { getTimezoneAbbreviation } from "../../lib/utils";

type TrackingStage = "scheduled" | "received" | "preparing" | "ready" | "out_for_delivery";

interface TrackedOrder {
  orderNumber: string;
  stage: TrackingStage;
  scheduledAt?: string;
  timezone?: string;
  storeName?: string;
}

const REFRESH_MS = 30 * 1000;

const STEPS: { stage: TrackingStage; label: string }[] = [
  { stage: "received", label: "Order received" },
  { stage: "preparing", label: "Preparing your order" },
  { stage: "ready", label: "Ready for delivery" },
  { stage: "out_for_delivery", label: "Out for delivery" },
];

const formatScheduledTime = (scheduledAt: string, timezone?: string) => {
  const formatted = new Date(scheduledAt).toLocaleString("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return timezone ? `${formatted} ${getTimezoneAbbreviation(timezone)}` : formatted;
};

const TrackOrder: React.FC = () => {
  const [searchParams] = useSearchParams();
  // /track?order=ABC123 (from the order confirmation page) looks the order up straight away
  const orderFromLink = (searchParams.get("order") || "").trim();
  const [orderNumber, setOrderNumber] = useState(orderFromLink);
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchOrder = useCallback(async (number: string) => {
    try {
      const { data } = await trackOrder(number);
      setOrder(data.data);
      setError("");
    } catch (err: any) {
      setOrder(null);
      setError(
        err?.response?.status === 404
          ? "No active delivery found for this order number. Completed and cancelled orders can't be tracked."
          : "Could not check this order. Please try again.",
      );
    }
  }, []);

  // Keep the status current while the customer has the page open
  const trackedNumber = order?.orderNumber;
  useEffect(() => {
    if (!trackedNumber) return;
    const timer = setInterval(() => fetchOrder(trackedNumber), REFRESH_MS);
    return () => clearInterval(timer);
  }, [trackedNumber, fetchOrder]);

  useEffect(() => {
    if (!orderFromLink) return;
    setLoading(true);
    fetchOrder(orderFromLink).finally(() => setLoading(false));
  }, [orderFromLink, fetchOrder]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const number = orderNumber.trim();
    if (!number || loading) return;
    setLoading(true);
    await fetchOrder(number);
    setLoading(false);
  };

  const activeStep = order ? STEPS.findIndex((step) => step.stage === order.stage) : -1;

  return (
    <div className="dm-order-modal min-h-screen flex justify-center px-4 py-10 sm:py-16">
      <div className="w-full max-w-md">
        <h1 className="text-2xl font-semibold text-black m-0">Track Your Delivery</h1>
        <p className="text-sm text-gray-600 mt-2">
          Enter your order number to see the status of your delivery.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 flex gap-2">
          <input
            type="text"
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
            placeholder="Order number, e.g. ABC1234"
            maxLength={31}
            aria-label="Order number"
            className="flex-1 min-w-0 px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-gray-300"
          />
          <button
            type="submit"
            disabled={loading || !orderNumber.trim()}
            className="dm-white-btn px-5 py-3 rounded-2xl font-semibold bg-black toggle-selected-text disabled:opacity-50"
          >
            {loading ? "Checking..." : "Track"}
          </button>
        </form>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        {order && (
          <div className="mt-6 border border-gray-200 rounded-2xl p-5">
            <p className="text-lg font-semibold text-black m-0">Order #{order.orderNumber}</p>
            {order.storeName && (
              <p className="text-sm text-gray-600 mt-1 mb-0">{order.storeName}</p>
            )}
            {order.scheduledAt && (
              <p className="text-sm text-gray-600 mt-1 mb-0">
                Scheduled for {formatScheduledTime(order.scheduledAt, order.timezone)}
              </p>
            )}

            <ol className="mt-5 mb-0 p-0 list-none flex flex-col gap-4">
              {STEPS.map((step, index) => {
                const done = index <= activeStep;
                return (
                  <li key={step.stage} className="flex items-center gap-3">
                    <span
                      className={`w-3 h-3 rounded-full flex-shrink-0 ${
                        done ? "bg-green-500" : "bg-gray-300"
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        index === activeStep
                          ? "font-semibold text-black"
                          : done
                            ? "text-black"
                            : "text-gray-500"
                      }`}
                    >
                      {step.label}
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
